import {
  GoogleAnalytics,
  Hubspot,
  Marketo,
  Salesforce,
  SegmentCompany as Segment,
} from '@clearkit/icons';
import { Subscribe } from 'unstated';

import ActiveIntegrationsList from '~/components/ActiveIntegrationsList';
import { BannerUpgradePlan } from '~/components/CTABanner/BannerUpgradePlan';
import IntegrationPickerCard from '~/components/IntegrationPickerCard';
import {
  IntegrationPickerLayout,
  IntegrationPickerRow,
} from '~/components/layouts/IntegrationPickerLayout';
import SidebarDrawer from '~/components/SidebarDrawer';
import UpgradeAccountForm from '~/components/UpgradeAccountForm';
import UserContainer from '~/containers/User';
import { AccountFlagEnum } from '~/generated/graphql';
import useFeatureFlag from '~/hooks/account/useFeatureFlag';
import {
  useHasConversionsAccess,
  useHasLegacyPlan,
  useHasOperationsAccess,
} from '~/hooks/account/usePlatformAccess';
import Tile from '~/images/Tiles/Enrichment.svg';

import LegacyEnrichmentList from './LegacyEnrichmentList';

const EnrichmentList = () => {
  const [showUpgradeForm, setShowUpgradeForm] = useState(false);

  function showUpgradeAccountForm() {
    setShowUpgradeForm(true);
  }

  const hasHubspotFeatureFlag = useFeatureFlag('x_data_enrichment_hubspot');
  const hasMarketoFeatureFlag = useFeatureFlag('x_marketo_on_platform_beta');
  const hasLegacyPlan = useHasLegacyPlan();
  const hasOperationsAccess = useHasOperationsAccess();
  const hasConversionsAccess = useHasConversionsAccess();
  const hasBothAddons = hasConversionsAccess && hasOperationsAccess;
  const hasDataHealthFlag = useFeatureFlag('x_enrichment_health');

  const allowEnrichmentUi = useFeatureFlag(
    AccountFlagEnum.AllowDataEnrichmentPreview,
  ).enabled;
  const enrichmentDisabled = useFeatureFlag(AccountFlagEnum.XDataEnrichment)
    .disabled;
  // userPreviewingFeature - a free_tier user can accerss the UI for CRM enrichment but cant actually enable the feature
  const userPreviewingFeature = allowEnrichmentUi && enrichmentDisabled;
  const allowPlatformHubspotConfig = useFeatureFlag(
    AccountFlagEnum.AllowPlatformHubspotConfig,
  ).enabled;
  const allowPlatformMarketoConfig = useFeatureFlag(
    AccountFlagEnum.AllowPlatformMarketoConfig,
  ).enabled;
  const allowPlatformSegmentConfig = useFeatureFlag(
    AccountFlagEnum.AllowPlatformSegmentConfig,
  ).enabled;
  const allowPlatformSalesforceConfig = useFeatureFlag(
    AccountFlagEnum.AllowPlatformSalesforceConfig,
  ).enabled;
  const allowPlatformGoogleAnalyticsConfig = useFeatureFlag(
    AccountFlagEnum.AllowPlatformGoogleAnalyticsConfig,
  ).enabled;

  if (hasLegacyPlan) {
    return <LegacyEnrichmentList />;
  }

  const hubspotUrl =
    hasHubspotFeatureFlag.enabled || userPreviewingFeature
      ? '/enrichment/hubspot'
      : 'hubspot.clearbit.com';
  const hubspotVariant =
    hasHubspotFeatureFlag.enabled || userPreviewingFeature
      ? IntegrationPickerCard.Variants.default
      : IntegrationPickerCard.Variants.external;

  const marketoUrl =
    hasMarketoFeatureFlag.enabled || userPreviewingFeature
      ? '/enrichment/marketo'
      : 'marketo.clearbit.com';

  const marketoVariant =
    hasMarketoFeatureFlag.enabled || userPreviewingFeature
      ? IntegrationPickerCard.Variants.default
      : IntegrationPickerCard.Variants.external;

  const CONVERSIONS = [
    {
      Icon: Segment,
      type: 'segment',
      bgClass: 'bg-segment',
      title: 'Segment',
      text: 'Enrich people and companies in Segment with Clearbit data.',
      tag: 'Convert Intent',
      product: 'google_analytics',
      url: 'segment.clearbit.com',
      variant: IntegrationPickerCard.Variants.external,
      hasAccess: hasConversionsAccess || allowPlatformSegmentConfig,
    },
    {
      Icon: GoogleAnalytics,
      type: 'google_analytics',
      bgClass: 'bg-yellow',
      title: 'Google Analytics',
      text:
        'Enrich people and companies in Google Analytics with Clearbit data.',
      tag: 'Convert Intent',
      product: 'google_analytics',
      url: '/settings/configuration/google',
      hasAccess: hasConversionsAccess || allowPlatformGoogleAnalyticsConfig,
    },
  ];

  const OPERATIONS = [
    {
      Icon: Salesforce,
      type: 'salesforce',
      bgClass: 'bg-salesforce',
      title: 'Salesforce',
      text:
        'Enrich any account, contact, or lead, or your full database of records, within Salesforce.',
      tag: 'Accelerate Revenue',
      product: 'data_enrichment',
      url: '/enrichment/salesforce',
      hasAccess:
        hasOperationsAccess ||
        allowPlatformSalesforceConfig ||
        userPreviewingFeature,
    },
    {
      Icon: Salesforce,
      type: 'pardot',
      iconFillOverride: 'text-salesforce',
      bgClass: 'bg-gray-0',
      title: 'Pardot',
      text:
        'Enrich any account, contact, or lead, or your full database of records, within Pardot.',
      tag: 'Accelerate Revenue',
      product: 'data_enrichment',

      url: 'pardot.clearbit.com',
      variant: IntegrationPickerCard.Variants.external,
      hasAccess: hasOperationsAccess || allowPlatformSalesforceConfig,
    },
    {
      Icon: Marketo,
      type: 'marketo',
      bgClass: 'bg-marketo',
      title: 'Marketo',
      text:
        'Enrich any account, contact, or lead, or your full database of records, within Marketo.',
      tag: 'Accelerate Revenue',
      product: 'data_enrichment',
      url: marketoUrl,
      variant: marketoVariant,
      hasAccess:
        hasOperationsAccess ||
        allowPlatformMarketoConfig ||
        userPreviewingFeature,
    },
    {
      Icon: Hubspot,
      type: 'hubspot',
      bgClass: 'bg-hubspot',
      title: 'Hubspot',
      text:
        'Enrich and refresh new contacts, form submissions, or your entire contact database within HubSpot.',
      tag: 'Accelerate Revenue',
      product: 'data_enrichment',
      url: hubspotUrl,
      variant: hubspotVariant,
      hasAccess:
        hasOperationsAccess ||
        allowPlatformHubspotConfig ||
        userPreviewingFeature,
    },
  ];

  const accesibleIntegrations = { title: '', cards: [] };
  const unaccesibleIntegrations = { title: '', cards: [] };

  function assignIntegrations(arr = []) {
    arr.forEach((item) => {
      if (item.hasAccess) {
        accesibleIntegrations.cards.push(item);
      } else {
        unaccesibleIntegrations.cards.push(item);
      }
    });
  }

  assignIntegrations(OPERATIONS);
  assignIntegrations(CONVERSIONS);

  if (hasBothAddons) {
    accesibleIntegrations.title = 'Convert Intent & Accelerate Revenue';
  } else if (hasConversionsAccess) {
    accesibleIntegrations.title = 'Convert Intent';
    unaccesibleIntegrations.title = 'Accelerate Revenue';
  } else {
    accesibleIntegrations.title = 'Accelerate Revenue';
    unaccesibleIntegrations.title = 'Convert Intent';
  }
  return (
    <Subscribe to={[UserContainer]}>
      {(userContainer) => {
        return (
          <IntegrationPickerLayout
            background="white"
            subtitle="Get rich firmographic data into the systems that matter most to your business"
            tile={<Tile />}
            title="Enrichment"
          >
            <div className="flex flex-col flex-grow">
              {hasDataHealthFlag.enabled && <ActiveIntegrationsList />}
              {userPreviewingFeature ? (
                <BannerUpgradePlan className="px-10 py-6" />
              ) : null}
              {accesibleIntegrations.cards.length > 0 && (
                <IntegrationPickerRow
                  subtitle="Select your desired integration to get started."
                  title={`${accesibleIntegrations.title} integrations`}
                >
                  {accesibleIntegrations.cards.map((integration) => {
                    const objectType = 'enrichment';
                    const objectAction = 'update';
                    const objectSubtype = integration.type;

                    const hasPermission = userContainer.hasPermissionTo({
                      objectAction,
                      objectType,
                      objectSubtype,
                    });

                    return (
                      <IntegrationPickerCard
                        container={userContainer}
                        key={integration.title}
                        {...integration}
                        hasAccess
                        hasPermission={hasPermission}
                      />
                    );
                  })}
                </IntegrationPickerRow>
              )}
              {unaccesibleIntegrations.cards.length > 0 && (
                <IntegrationPickerRow
                  secondary
                  subtitle="Select your desired integration to get started."
                  title={`${unaccesibleIntegrations.title} integrations`}
                >
                  {unaccesibleIntegrations.cards.map((integration) => {
                    return (
                      <IntegrationPickerCard
                        key={integration.title}
                        {...integration}
                        hasAccess={integration.hasAccess}
                        setShowUpgradeForm={() =>
                          showUpgradeAccountForm(integration.product)
                        }
                      />
                    );
                  })}
                </IntegrationPickerRow>
              )}
            </div>

            <SidebarDrawer
              onClose={() => {
                setShowUpgradeForm(false);
              }}
              open={showUpgradeForm}
            >
              <UpgradeAccountForm
                onClose={() => {
                  setShowUpgradeForm(false);
                }}
              />
            </SidebarDrawer>
          </IntegrationPickerLayout>
        );
      }}
    </Subscribe>
  );
};

export default EnrichmentList;
